import {
  Box,
  FieldRichText,
  FormField,
  FormGrid,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { useCallback } from 'react';
import { connect } from 'react-redux';
import {
  Field,
  Form,
  FormErrors,
  formValueSelector,
  InjectedFormProps,
  reduxForm,
} from 'redux-form';
import { v4 as uuid } from 'uuid';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { SanitizeHTML } from 'app/utils/htmlSanitizer';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import {
  FlowCategoriesT,
  getAvailableFlowVariables,
} from 'components/powerFlowShared/helpers/flowVariableHelpers';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FlowMessengerNodeMessengerMessage } from 'components/settings/components/sections/dixaMessenger/views/flow/flow.types';
import { FlowManagerEditModalComponentT } from 'fable/flowEditor';

import { useElevioIntegration } from '../../../../elevioIntegrationProvider';
import { useParsedHtmlTextContent } from '../../../../hooks/useParsedHtmlTextContent';
import { useElevioArticleQuickResponse } from '../../../shared/knowledgeBaseLinkPreview/hooks/useElevioRichTextInputQuickResponse';
import { extractRichTextContentWithHtmlTags } from '../../../shared/utils/extractRichTextContentWithHtmlTags';
import { FlowVariableSelector } from '../variableSelector';

import { MessengerMessagePreview } from './components/messengerMessagePreview';
import { formatVariablesForTextInput } from './formatVariablesForTextInput';
import styles from './messageModal.module.scss';

interface Validation {
  text?: string;
}

interface MessageModalFormValues {
  text: string;
}

interface OwnProps
  extends FlowManagerEditModalComponentT<FlowMessengerNodeMessengerMessage> {
  text: string;
}

type Props = OwnProps & InjectedFormProps<MessageModalFormValues, OwnProps>;

const validate = (values: MessageModalFormValues): FormErrors => {
  const errors: Validation = {};

  if (!values) {
    return errors;
  }

  if (!values.text) {
    errors.text = 'Message is required';
  }

  return errors;
};

const MessageModalImpl = (props: Props): JSX.Element => {
  const {
    flowType,
    scopeVars,
    handleSubmit,
    node,
    nodeLabel,
    replaceNode,
    triggerClose,
    destroy,
    text,
    dirty: isChanged,
  } = props;

  const { elevioIntegration } = useElevioIntegration();
  const hasElevioIntegration = Boolean(elevioIntegration?.companyId);

  const availableFlowVariables: FlowCategoriesT = getAvailableFlowVariables({
    flowType,
    scopeVars,
  });

  const handleSaveClick = useCallback(
    (values: MessageModalFormValues): void => {
      const nodeOptions: FlowMessengerNodeMessengerMessage = {
        ...node,
        badges: [],
        childIds: [],
        icon: '',
        isAction: false,
        isEditable: true,
        isRemovable: true,
        nodeType: FlowNodeTypes.MESSENGER_MESSAGE,
        overlay: [],
        payload: {
          text: values.text,
          nextNodeId: node.payload?.nextNodeId || uuid(),
        },
        text: '',
      };

      replaceNode(nodeOptions);
      triggerClose();
    },
    [node, replaceNode, triggerClose],
  );

  const closeModal = useCallback(() => {
    destroy();
    triggerClose();
  }, [destroy, triggerClose]);

  const { quickResponseApi, quickResponseRenderer } =
    useElevioArticleQuickResponse({
      search: useParsedHtmlTextContent(text),
    });

  return (
    <Box width="800px">
      <ModalHead title={nodeLabel} onClose={closeModal}>
        This action allows you to send automated replies to customers following
        any action.
      </ModalHead>
      <Form onSubmit={handleSubmit(handleSaveClick)}>
        <ModalBody>
          <FormGrid className={styles.formGrid}>
            <FormField span={3}>
              <Field
                component={FieldRichText}
                name="text"
                label="Your message"
                mode="textarea"
                className={styles.editor}
                inputClassName={styles.editorInput}
                toolbarAddons={
                  <FlowVariableSelector
                    variables={formatVariablesForTextInput(
                      availableFlowVariables,
                    )}
                  />
                }
                quickResponseIcon="elevio"
                quickResponsesApi={
                  hasElevioIntegration ? quickResponseApi : undefined
                }
                renderQuickResponse={
                  hasElevioIntegration ? quickResponseRenderer : undefined
                }
                sanitize={SanitizeHTML.sanitizerForChannel('messenger')}
                languages={elevioIntegration?.availableLocales ?? []}
                editorContentExtractor={extractRichTextContentWithHtmlTags}
              />
            </FormField>
            <FormField span={3}>
              <MessengerMessagePreview
                variables={availableFlowVariables}
                text={text}
              />
            </FormField>
          </FormGrid>
        </ModalBody>
        <ModalFoot
          confirmOnClick={handleSubmit(handleSaveClick)}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={closeModal}
        />
      </Form>
    </Box>
  );
};

const formName = 'messengerMessageModal';

const mapStateToProps = (state: RootReducerStateT, ownProps: OwnProps) => {
  const { payload } = ownProps.node;
  const selector = formValueSelector(formName);

  return {
    initialValues: {
      text: payload?.text || '',
    },
    text: selector(state, 'text'),
  };
};

export const MessageModal = connect(mapStateToProps)(
  reduxForm<MessageModalFormValues, OwnProps>({
    enableReinitialize: true,
    form: formName,
    touchOnBlur: false,
    validate,
  })(MessageModalImpl),
);
